// src/app/components/trips/listCard/ListCard.js
import React, { useEffect, useState } from "react";
import { Card } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import cls from "classnames";

import { useToggleFavorite } from "../favorites/hooks/useToggleFavorite";
import { useZorni } from "../../../hooks/useZorni";
import { useRequireAuth } from "../../../hooks/useRequireAuth";
import { SkeletonCard } from "../skeletonCard/SkeletonCard";
import { useVisitedTripStorage } from "../../tripDetails/detailsTrip/hooks/useVisitedTrips";
import { resolveLocations } from "../../../pages/travelResult/searchResults";
import {
  ImpressionTracker,
  ContextPage,
  useFavoriteTracking,
} from "../../../../tracking";
import styles from "./ListCard.module.css";

/**
 * Cartes de la page résultats (.results-collection) : la page courante,
 * déjà filtrée, triée et découpée par useSearchResults. Vue « grid »
 * (3 colonnes) ou « list » (image à gauche).
 */
const ListCard = ({ items = [], view = "grid", loading = false, adult, child, baby }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useZorni();
  const requireAuth = useRequireAuth();
  const { toggleFavorite } = useToggleFavorite();
  const { addVisitedTrip } = useVisitedTripStorage(null);
  const { trackAddToFavorites } = useFavoriteTracking({ page: ContextPage.TRIPS_LIST });

  /* Copie locale pour basculer le favori tout de suite, avant la réponse. */
  const [cards, setCards] = useState(items);
  useEffect(() => setCards(items), [items]);

  const handleUpdateFavorite = async (item) => {
    const wasFavorite = Boolean(item?.isFavorite);

    await requireAuth(async () => {
      setCards((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, isFavorite: !p.isFavorite } : p))
      );
      await toggleFavorite(userId, item);

      if (!wasFavorite) {
        await trackAddToFavorites({
          establishmentId: item?.establishmentId,
          publicationId: item?.id,
        });
      }
    });
  };

  const handleClick = (item) => {
    addVisitedTrip(item);
    const params = new URLSearchParams();
    if (Number(adult) > 0) params.set("nbAdults", adult);
    if (Number(child) > 0) params.set("nbChilds", child);
    if (Number(baby) > 0) params.set("nbBabies", baby);
    const queryString = params.toString();
    navigate(`/trips/${item.id}/${item.tripDateId}${queryString ? `?${queryString}` : ""}`);
  };

  const gridClass = cls(styles.collection, view === "list" ? styles.viewList : styles.viewGrid);

  if (loading) {
    return (
      <div className={gridClass} aria-busy="true">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={i} type={view === "list" ? "liste" : "grille"} />
        ))}
      </div>
    );
  }

  return (
    <div className={gridClass}>
      {cards.map((item) => {
        const locations = resolveLocations(item);
        return (
          <ImpressionTracker
            key={item.id}
            publicationId={item.id}
            establishmentId={item.establishmentId}
            page={ContextPage.TRIPS_LIST}
          >
            <Card
              t={t}
              type="offer"
              layout={view}
              item={{ ...item, city: item?.city || locations.join(" · "), locations }}
              onClick={() => handleClick(item)}
              onUpdateFavorite={() => handleUpdateFavorite(item)}
            />
          </ImpressionTracker>
        );
      })}
    </div>
  );
};

export default ListCard;
